// 'use client';

// import * as React from 'react';

// import config from 'configs/app';
// import getAddEthereumChainParams from './getAddEthereumChainParams';
// import { useAccount } from 'wagmi';
// import { useMultichainContext } from 'lib/contexts/multichain';

// function isUnrecognizedChainError(err: any) {
//   const code = err?.code;
//   const msg = String(err?.message ?? '').toLowerCase();

//   return (
//     code === 4902 ||
//     msg.includes('4902') ||
//     msg.includes('unrecognized chain') ||
//     msg.includes('not added')
//   );
// }

// function normalizeHexChainId(v: any) {
//   if (typeof v === 'string') return v.toLowerCase();
//   return String(v ?? '').toLowerCase();
// }

// async function getChainId(provider: any) {
//   const id = await provider.request({ method: 'eth_chainId' });
//   return normalizeHexChainId(id);
// }

// export default function useEnsureChain() {
//   const { connector } = useAccount();
//   const multichainContext = useMultichainContext();
//   const chainConfig = multichainContext?.chain.config ?? config;

//   return React.useCallback(async () => {
//     if (!connector) throw new Error('No active wallet connector');

//     const provider = (await connector.getProvider()) as any;
//     if (!provider || typeof provider.request !== 'function') {
//       throw new Error('Invalid EIP-1193 provider');
//     }

//     const addParams = getAddEthereumChainParams(chainConfig);
//     const targetChainId = normalizeHexChainId(addParams.chainId);

//     const before = await getChainId(provider);
//     if (before === targetChainId) return { changed: false };

//     try {
//       await provider.request({
//         method: 'wallet_switchEthereumChain',
//         params: [{ chainId: addParams.chainId }],
//       });
//     } catch (err: any) {
//       if (!isUnrecognizedChainError(err)) throw err;

//       await provider.request({
//         method: 'wallet_addEthereumChain',
//         params: [addParams],
//       });

//       try {
//         await provider.request({
//           method: 'wallet_switchEthereumChain',
//           params: [{ chainId: addParams.chainId }],
//         });
//       } catch { }
//     }

//     const after = await getChainId(provider);
//     if (after !== targetChainId) {
//       throw new Error(
//         'Switch request sent, but network not changed yet. Please open your wallet and approve the request.'
//       );
//     }

//     return { changed: true };
//   }, [connector, chainConfig]);
// }
'use client';

import * as React from 'react';
import { useAccount } from 'wagmi';

import config from 'configs/app';
import { useMultichainContext } from 'lib/contexts/multichain';

import getAddEthereumChainParams from './getAddEthereumChainParams';

function isUnrecognizedChainError(err: any) {
  const code = err?.code;
  const msg = String(err?.message ?? '').toLowerCase();

  return (
    code === 4902 ||
    msg.includes('4902') ||
    msg.includes('unrecognized chain') ||
    msg.includes('not added')
  );
}

function isUserRejectedError(err: any) {
  const code = err?.code;
  const msg = String(err?.message ?? '').toLowerCase();

  return (
    code === 4001 ||
    msg.includes('user rejected') ||
    msg.includes('user denied') ||
    msg.includes('rejected')
  );
}

function normalizeHexChainId(v: any) {
  return String(v ?? '').toLowerCase();
}

async function getChainId(provider: any) {
  const id = await provider.request({ method: 'eth_chainId' });
  return normalizeHexChainId(id);
}

async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForChainId(
  provider: any,
  targetChainId: string,
  timeoutMs = 10000,
  intervalMs = 500,
) {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    try {
      const current = await getChainId(provider);
      if (current === targetChainId) return true;
    } catch {
      // ignore temporary provider errors
    }

    await sleep(intervalMs);
  }

  return false;
}

export default function useEnsureChain() {
  const { connector } = useAccount();
  const multichainContext = useMultichainContext();
  const chainConfig = multichainContext?.chain.config ?? config;

  return React.useCallback(async() => {
    if (!connector) throw new Error('No active wallet connector');

    const provider = (await connector.getProvider()) as any;
    if (!provider || typeof provider.request !== 'function') {
      throw new Error('Invalid EIP-1193 provider');
    }

    const addParams = getAddEthereumChainParams(chainConfig);
    const targetChainId = normalizeHexChainId(addParams.chainId);

    const before = await getChainId(provider);
    if (before === targetChainId) {
      return { changed: false };
    }

    // 1) Try direct switch first
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [ { chainId: addParams.chainId } ],
      });

      const switched = await waitForChainId(provider, targetChainId, 8000, 400);
      if (switched) return { changed: true };

      throw new Error(
        'Network switch was requested, but the wallet has not applied it yet. Please check your wallet app.',
      );
    } catch (err: any) {
      if (isUserRejectedError(err)) {
        throw new Error('You rejected the network switch request.');
      }

      if (!isUnrecognizedChainError(err)) {
        throw err;
      }
    }

    // 2) Chain is not added -> add it
    try {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [ addParams ],
      });
    } catch (err: any) {
      if (isUserRejectedError(err)) {
        throw new Error('You rejected the network add request.');
      }
      throw err;
    }

    // Give mobile wallets a moment after add
    await sleep(1200);

    // 3) Some wallets auto-switch after add
    const afterAdd = await getChainId(provider);
    if (afterAdd === targetChainId) {
      return { changed: true };
    }

    // 4) If not switched yet, try one more explicit switch
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [ { chainId: addParams.chainId } ],
      });
    } catch (err: any) {
      if (isUserRejectedError(err)) {
        throw new Error('You rejected the network switch request.');
      }

      // on some mobile wallets this may fail silently / not open UI
      // we still continue to poll once more before giving up
    }

    const finalSwitch = await waitForChainId(provider, targetChainId, 10000, 500);
    if (!finalSwitch) {
      throw new Error(
        'Could not switch network automatically. Please open MetaMask and switch network manually.',
      );
    }

    return { changed: true };
  }, [ connector, chainConfig ]);
}
